import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateOdooActivity = ({ user }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'beginner',
    time_limit_minutes: 60,
    custom_objectives: [],
    is_public: true,
    assigned_courses: []
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Course selector states
  const [showCourseSelector, setShowCourseSelector] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchCourses();
  }, []);
  
  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const response = await axios.get(`${BACKEND_URL}/api/classroom/courses/${user.id}`);
      setAvailableCourses(response.data.courses || []);
    } catch (err) {
      console.error('Error fetching courses:', err);
      // No mostrar error al usuario, simplemente no habrá cursos disponibles
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/odoo-activities/templates`);
      setTemplates(response.data.templates || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error al cargar plantillas');
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateKey) => {
    setSelectedTemplate(templateKey);
    const template = templates.find(t => t.key === templateKey);
    if (template) {
      setFormData(prev => ({
        ...prev,
        title: template.title,
        description: template.description,
        difficulty: template.difficulty
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'time_limit_minutes' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData,
        template_key: selectedTemplate,
        teacher_id: user.email,
        is_public: formData.is_public,
        assigned_courses: formData.is_public ? [] : formData.assigned_courses
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/odoo-activities`,
        payload
      );

      setSuccess('Actividad Odoo creada correctamente');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (err) {
      console.error('Error creating activity:', err);
      setError(err.response?.data?.detail || 'Error al crear la actividad');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-white text-xl">Cargando plantillas...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                🏢 Crear Actividad Odoo ERP
              </h1>
              <p className="text-white/70">
                Crea actividades prácticas de configuración y gestión en Odoo
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all"
            >
              ← Volver
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 text-red-200 p-4 rounded-xl mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/20 border border-green-500/50 text-green-200 p-4 rounded-xl mb-4">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Template Selection */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-4">
              📋 Plantillas Disponibles
            </h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {templates.map((template) => (
                <div
                  key={template.key}
                  onClick={() => handleTemplateSelect(template.key)}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    selectedTemplate === template.key
                      ? 'bg-purple-500/50 border-2 border-purple-400'
                      : 'bg-white/5 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-white">{template.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      template.difficulty === 'beginner' ? 'bg-green-500/30 text-green-200' :
                      template.difficulty === 'intermediate' ? 'bg-yellow-500/30 text-yellow-200' :
                      'bg-red-500/30 text-red-200'
                    }`}>
                      {template.difficulty === 'beginner' ? 'Fácil' :
                       template.difficulty === 'intermediate' ? 'Medio' : 'Avanzado'}
                    </span>
                  </div>
                  <p className="text-sm text-white/70 mb-2">{template.description}</p>
                  <div className="flex gap-4 text-xs text-white/60">
                    <span>🎯 {template.objectives_count} objetivos</span>
                    <span>⭐ {template.total_score} puntos</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Activity Form */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-4">
              ⚙️ Configuración
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-white/80 mb-2 text-sm">Título</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
                  required
                />
              </div>

              <div>
                <label className="block text-white/80 mb-2 text-sm">Descripción</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Dificultad</label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:border-purple-400"
                  >
                    <option value="beginner" className="bg-purple-900">Principiante</option>
                    <option value="intermediate" className="bg-purple-900">Intermedio</option>
                    <option value="advanced" className="bg-purple-900">Avanzado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white/80 mb-2 text-sm">
                    Tiempo límite (min)
                  </label>
                  <input
                    type="number"
                    name="time_limit_minutes"
                    value={formData.time_limit_minutes}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>

              {/* Public/Private Assignment Section */}
              <div className="border-t border-white/20 pt-4 mt-4">
                <label className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      is_public: e.target.checked,
                      assigned_courses: e.target.checked ? [] : prev.assigned_courses
                    }))}
                    className="w-5 h-5 rounded border-white/30 text-purple-500 focus:ring-purple-500 bg-white/10"
                  />
                  <span className="text-white font-medium">Actividad Pública</span>
                  <span className="text-white/50 text-sm">- Cualquier estudiante puede acceder</span>
                </label>

                {!formData.is_public && (
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <label className="block text-white/80 mb-3 text-sm">
                      📚 Asignar a Clases de Google Classroom
                    </label>
                    
                    <button
                      type="button"
                      onClick={() => setShowCourseSelector(!showCourseSelector)}
                      className="px-4 py-2 bg-blue-500/80 hover:bg-blue-500 text-white rounded-lg transition flex items-center gap-2 text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {formData.assigned_courses.length > 0 
                        ? `${formData.assigned_courses.length} clase(s) asignada(s)` 
                        : 'Seleccionar clases'}
                    </button>

                    {showCourseSelector && (
                      <div className="mt-3 p-3 bg-black/20 rounded-lg max-h-48 overflow-y-auto">
                        {loadingCourses ? (
                          <p className="text-white/60 text-sm">Cargando clases...</p>
                        ) : availableCourses.length === 0 ? (
                          <p className="text-white/60 text-sm">No hay clases disponibles. Conecta Google Classroom primero.</p>
                        ) : (
                          <div className="space-y-2">
                            {availableCourses.map((course) => (
                              <label key={course.id} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.assigned_courses.includes(course.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData(prev => ({
                                        ...prev,
                                        assigned_courses: [...prev.assigned_courses, course.id]
                                      }));
                                    } else {
                                      setFormData(prev => ({
                                        ...prev,
                                        assigned_courses: prev.assigned_courses.filter(id => id !== course.id)
                                      }));
                                    }
                                  }}
                                  className="w-4 h-4 rounded border-white/30 text-purple-500 focus:ring-purple-500 bg-white/10"
                                />
                                <span className="text-white/90 text-sm">{course.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Selected courses display */}
                    {formData.assigned_courses.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {formData.assigned_courses.map((courseId) => {
                          const course = availableCourses.find(c => c.id === courseId);
                          return course ? (
                            <span key={courseId} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-purple-500/30 text-purple-200 border border-purple-400/30">
                              {course.name}
                              <button
                                type="button"
                                onClick={() => setFormData(prev => ({
                                  ...prev,
                                  assigned_courses: prev.assigned_courses.filter(id => id !== courseId)
                                }))}
                                className="ml-2 hover:text-white"
                              >
                                ×
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={saving || !selectedTemplate || (!formData.is_public && formData.assigned_courses.length === 0)}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    saving || !selectedTemplate || (!formData.is_public && formData.assigned_courses.length === 0)
                      ? 'bg-gray-500/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {saving ? '⏳ Creando...' : '✨ Crear Actividad Odoo'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6 mt-6">
          <h3 className="text-lg font-semibold text-blue-200 mb-2">
            💡 ¿Cómo funcionan las Actividades Odoo?
          </h3>
          <ul className="text-blue-100/80 space-y-2 text-sm">
            <li>• Cada alumno recibe su propia instancia de Odoo en un contenedor Docker</li>
            <li>• El alumno completa tareas reales: configurar empresa, crear productos, gestionar ventas...</li>
            <li>• El sistema verifica automáticamente si los objetivos se han completado</li>
            <li>• Los contenedores persisten para que el alumno pueda reconectarse más tarde</li>
            <li>• El profesor puede monitorear el progreso de todos los alumnos en tiempo real</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CreateOdooActivity;